// domain/models.ts

export type Id = number;

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export type TradingProcedureStatus =
    | 'Draft'
    | 'Published'
    | 'Active'
    | 'AwaitingDecision'
    | 'Finished'
    | 'Cancelled';

export type ParticipantRole = 'seller' | 'buyer';

export enum TradingProcedureType {
    English = 'english',
    Sealed = 'sealed',
    Unknown = 'unknown',
}

export interface TradingProcedure {
    id: number;
    type: TradingProcedureType;
    showSellerName: boolean;
    name: string | null;
    sellerOrganizationName: string | null;
    sellerOrganizationLogo: string | null;
    sellerOrganizationTypeName: string | null;
    sellerOrganizationId: number | null;
    startDateTime: Date | null;
    endDateTime: Date;
    createdDateTime: Date;
    auctionExtensionSeconds: number | null;
    status: TradingProcedureStatus;
    bidStepAmount: number | null;
    sellerDescription: string | null;
    platformNote: string | null;
    currency: string; // normalized ISO code or 'XXX' fallback

    lotCount: number;
    contractCount: number;
    debtAmount: number;
    principalAmount: number;
    averageDpd: number;
    minPrice: number;
    currentPrice: number | null;
}

export interface Lot {
    id: Id;
    name: string | null;
    tradingProcedureId: Id;
    minPrice: number;
    debtAmount: number;
    principalAmount: number;
    averageDpd: number;
    contractCount: number;
    documentType: string;
    portfolioType: string;
    debtStage: string;
    finalOfferId: number | null;
}

export interface OfferResult {
    id: number;
    lotId: number;
    organizationId: number;
    amount: number;
    postedDateTime: string;
}

export interface AutoBid {
    id: Id;
    lotId: Id;
    organizationId: number;
    targetIteration: number;
    createdDateTime: Date;
}

export interface SetAutoBidPayload {
    lotId: Id;
    targetIteration: number;
}

export type AuctionStatus = 'active' | 'finished';

export interface Auction {
    id: Id;
    lotId: Id;
    status: AuctionStatus;
    currentIteration: number;
    endDateTime: Date;
    leadingOrganizationId: Id | null;
}

/**
 * Текущее предложение организации по лоту.
 *
 * **Важно:** Двойное хранение `amount` и `iteration` — умышленное архитектурное решение.
 * Подробнее см. `README.md` раздел "Архитектурные решения".
 *
 * @see README.md раздел "Архитектурные решения" для объяснения двойного хранения
 */
export interface CurrentOrganizationOffer {
    lotId: number;
    /** Источник истины от бэкенда (общая модель для english и sealed аукционов) */
    amount: number;
    /** Вычисляемое значение для бизнес-логики english auction (нужно для создания ставки) */
    iteration: number | null;
}


export interface NotStartedAuction {
    kind: 'NotStartedAuction';
    lot: Lot;
    autobid: AutoBid | null;
}
export interface ActiveAuction {
    kind: 'ActiveAuction';
    lot: Lot;
    auction: Auction;
    autobid: AutoBid | null;
    currentOrganizationOffer: CurrentOrganizationOffer | null;
}
export interface FinishedAuction {
    kind: 'FinishedAuction';
    lot: Lot;
    auction: Auction;
    currentOrganizationOffer: CurrentOrganizationOffer | null;
}
export type LotProcess = NotStartedAuction | ActiveAuction | FinishedAuction;

export interface TradingProcedureSnapshot {
    organizationId: number,
    organizationName?: string | null,
    role: ParticipantRole,
    tradingProcedure: TradingProcedure,
    lots: Lot[],
    auctions: Auction[],
    autoBids: AutoBid[],
    offers: CurrentOrganizationOffer[],
}

export interface BidHistory {
    id: number;
    auctionId: number;
    organizationId: number;
    postedDateTime: string;
    iteration: number;
}

export interface OrganizationInfo {
    organizationId: number;
    organizationName: string | null;
}

// Events
export interface BidAccepted {
    auctionId: number;
    organizationId: number;
    iteration: number;
}

export interface AuctionEnded {
    id: Id;
}

export interface AuctionStarted {
    id: Id;
}

export interface AuctionExtended {
    id: Id;
    endDateTime: string;
}

export interface TradingProcedureStatusChanged {
    id: number;
    status: TradingProcedureStatus;
}

export interface BidHistoryCreated {
    id: number;
    auctionId: number;
    organizationId: number;
    postedDateTime: string;
    iteration: number;
}

export interface TradingProcedureDocument {
    id: number;
    tradingProcedureId: number;
    fileName: string | null;
    extension: string | null;
    documentUrl: string | null;
    description: string | null;
    contentLength: number;
    createdDateTime: string;
}

export type TradingProcedureRegistry = Omit<TradingProcedureDocument, 'id' | 'documentUrl'>
